<template>
  <div class="student-form-container">
    <div class="form-wrapper" :class="{ 'form-completed': completed }">
      <!-- Header Section -->
      <div class="header-section">
        <h1 class="form-title">
          Datos del estudiante
          <span v-if="completed" class="completed-badge">✓ Completado</span>
        </h1>
        <p class="form-subtitle">
          {{ completed ? 'Datos del estudiante registrados correctamente' : 'Ingresa los datos que faltan del estudiante' }}
        </p>
      </div>

      <!-- Form Fields -->
      <form 
        id="studentForm"
        @submit.prevent="handleSubmit" 
        class="form-content"
        autocomplete="off"
      >
        <!-- Row 1: Nombre del estudiante y RUT -->
        <div class="form-row">
          <div class="form-group form-field">
            <label class="field-label" for="studentName">Nombre del estudiante</label>
            <input
              v-model="form.studentName"
              type="text"
              id="studentName"
              name="studentName"
              class="form-input"
              placeholder="Nombre completo del estudiante"
              :disabled="disabled"
              maxlength="100"
            />
            <div class="errorContainer"></div>
          </div>
          <div class="form-group form-field">
            <label class="field-label" for="rutStudent">Rut</label>
            <input
              v-model="form.rut"
              type="text"
              id="rutStudent"
              name="rut"
              class="form-input"
              placeholder="00.000.000-K"
              :disabled="disabled"
              maxlength="12"
              @input="handleRutInput($event, 'rutStudent')"
              @blur="handleRutBlur($event, 'rutStudent')"
              @keypress="handleRutKeypress($event)"
            />
          </div>
        </div>

        <!-- Row 2: Email y Fecha de nacimiento -->
        <div class="form-row">
          <div class="form-group form-field">
            <label class="field-label" for="emailStudent">Mail</label>
            <input
              v-model="form.email"
              type="email"
              id="emailStudent"
              name="email"
              class="form-input"
              placeholder="example@email.com"
              :disabled="disabled"
              maxlength="50"
              @input="validateEmail"
              @blur="validateEmail"
            />
          </div>
          <div class="form-group form-field">
            <label class="field-label" for="birthDate">Fecha de nacimiento</label>
            <vue-mask
              placeholder="dd/mm/aaaa"
              mask="DA/MO/YEXR"
              :raw="false"
              v-model="form.birthDate"
              :options="dateMaskOptions"
              id="birthDate"
              name="birthDate"
              class="form-input"
              :disabled="disabled"
            />
            <div class="errorContainer"></div>
          </div>
        </div>

        <!-- Row 3: Celular y Región del colegio -->
        <div class="form-row">
          <div class="form-group form-field">
            <label class="field-label" for="phoneNumber">Celular</label>
            <div class="phone-input-container">
              <select class="country-code-select">
                <option value="+56">+56</option>
              </select>
              <input
                v-model="form.phoneNumber"
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                class="form-input phone-number-input"
                placeholder=""
                :disabled="disabled"
                maxlength="9"
                @input="validatePhone"
                @blur="validatePhone"
                @keyup="validatePhone"
              />
            </div>
          </div>
          <div class="form-group form-field">
            <label class="field-label" for="schoolRegion">Región del colegio</label>
            <multiselect
              v-model="selectedSchoolRegion"
              :options="availableRegions"
              id="schoolRegion"
              name="schoolRegion"
              placeholder="Selecciona la región del colegio"
              track-by="id"
              label="name"
              :showLabels="false"
              :disabled="disabled"
              class="multiselect-custom"
              @select="onSelectSchoolRegion"
            />
            <input
              autocomplete="none"
              class="withErrorContainer input-hidden"
              :value="form.schoolRegionId"
              :name="'schoolRegion-' + timestamp"
            />
            <div class="errorContainer"></div>
          </div>
        </div>

        <!-- Row 4: Comuna del colegio y Colegio -->
        <div class="form-row">
          <div class="form-group form-field">
            <label class="field-label" for="schoolCommune">Comuna del colegio</label>
            <multiselect
              v-model="selectedSchoolCommune"
              :options="availableCommunes"
              id="schoolCommune"
              name="schoolCommune"
              placeholder="Selecciona la comuna del colegio"
              track-by="id"
              label="name"
              :showLabels="false"
              :disabled="!form.schoolRegionId || disabled"
              class="multiselect-custom"
              @select="onSelectSchoolCommune"
            />
            <input
              autocomplete="none"
              class="withErrorContainer input-hidden"
              :value="form.schoolCommuneId"
              :name="'schoolCommune-' + timestamp"
            />
            <div class="errorContainer"></div>
          </div>
          <div class="form-group form-field">
            <label class="field-label" for="school">Colegio</label>
            <multiselect
              v-model="selectedSchool"
              :options="availableSchools"
              id="school"
              name="school"
              placeholder="Selecciona tu colegio"
              track-by="id"
              label="name"
              :showLabels="false"
              :disabled="!form.schoolCommuneId || disabled"
              class="multiselect-custom"
              @select="onSelectSchool"
            />
            <input
              autocomplete="none"
              class="withErrorContainer input-hidden"
              :value="form.schoolId"
              :name="'school-' + timestamp"
            />
            <div class="errorContainer"></div>
          </div>
        </div>

        <!-- Row 5: Año de egreso IV Medio y Promedio NEM -->
        <div class="form-row">
          <div class="form-group form-field">
            <label class="field-label" for="graduationYear">Año de egreso IV Medio</label>
            <multiselect
              v-model="selectedGraduationYear"
              :options="selectYears"
              id="graduationYear"
              name="graduationYear"
              placeholder="Selecciona año de egreso"
              track-by="value"
              label="label"
              :showLabels="false"
              :disabled="disabled"
              class="multiselect-custom"
              @select="onSelectGraduationYear"
            />
            <input
              autocomplete="none"
              class="withErrorContainer input-hidden"
              :value="form.graduationYear"
              :name="'graduationYear-' + timestamp"
            />
            <div class="errorContainer"></div>
          </div>
          <div class="form-group form-field">
            <label class="field-label" for="averageScore">Promedio NEM</label>
            <input
              v-model="form.averageScore"
              type="text"
              id="averageScore"
              name="averageScore"
              class="form-input"
              :disabled="disabled"
              maxlength="3"
              placeholder="Ej: 6,5"
              @input="validateAverageScore"
            />
            <div class="errorContainer"></div>
          </div>
        </div>

        <!-- Separator -->
        <div class="form-row">
          <div class="form-field full-width">
            <div class="form-separator"></div>
          </div>
        </div>

        <!-- Terms and Conditions Section -->
        <div class="form-row">
          <div class="form-field full-width">
            <div class="terms-conditions-section">
              <label class="terms-checkbox-container">
                <input
                  type="checkbox"
                  v-model="termsAccepted"
                  class="terms-checkbox"
                  :disabled="disabled"
                />
                <span class="checkmark"></span>
                <span class="terms-text">
                  Por este acto el estudiante y/o su apoderado/a declaran que leyeron el Contrato de Prestación de Servicios Educacionales
                  que se suscribe, así como la totalidad de sus anexos. Al mismo tiempo, 
                  declaran su completa conformidad con sus 
                  <a href="https://ventaonline.preupdv.cl/api/static/CONTRATO_DE_PRESTACION_DE_SERVICIOS_EDUCACIONALES_PDV.pdf" target="_blank" class="terms-link">Términos y Condiciones</a>
                </span>
              </label>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="form-row">
          <div class="form-field full-width">
            <div class="action-buttons">
              <button
                type="button"
                class="back-button"
                @click="$emit('go-back')"
              >
                <img src="/images/iconos/IconLeft.svg" alt="Volver" class="button-icon" />
                Volver
              </button>
              <button
                type="submit"
                class="continue-button"
                :disabled="!isFormValid || disabled"
              >
                {{ buttonText }}
                <img src="/images/iconos/IconRight.svg" alt="Continuar" class="button-icon" />
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import formValidation from "@/mixins/formValidation";
import rutValidation from "@/mixins/rutValidation";
import { orderBy } from "@/helpers";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";
import vueMask from "vue-jquery-mask";
import { VALIDATION_MESSAGES, VALIDATION_REGEX } from "@/constants/validationMessages";

global.jQuery = require("jquery");
const $ = global.jQuery;
window.$ = $;

export default {
  name: 'StudentForm',
  components: {
    Multiselect,
    vueMask,
  },
  mixins: [formValidation, rutValidation],
  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    completed: {
      type: Boolean,
      default: false
    },
    studentEmail: {
      type: String,
      default: ''
    },
    initialData: {
      type: Object,
      default: () => ({})
    },
    stepperData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      VALIDATION_MESSAGES,
      VALIDATION_REGEX,
      dataLoaded: false, // Flag para evitar cargar múltiples veces
      isSubmitting: false, // Flag para evitar múltiples envíos
      phoneError: false,
      timestamp: Date.now(),
      availableRegions: [],
      availableCommunes: [],
      availableSchools: [],
      selectedSchoolRegion: null,
      selectedSchoolCommune: null,
      selectedSchool: null,
      selectedGraduationYear: null,
      form: {
        studentName: '',
        rut: '',
        email: '',
        phoneNumber: '',
        birthDate: '',
        averageScore: '',
        schoolRegionId: null,
        schoolCommuneId: null,
        schoolId: null,
        graduationYear: null,
        authorizationPublicitary: true
      },
      termsAccepted: false,
      dateMaskOptions: {
        translation: {
          D: { pattern: /[0-3]/ },
          A: { pattern: /[0-9]/ },
          M: { pattern: /[0-1]/ },
          O: { pattern: /[0-9]/ },
          Y: { pattern: /[1-2]/ },
          E: { pattern: /[0-9]/ },
          X: { pattern: /[0-9]/ },
          R: { pattern: /[0-9]/ }
        }
      }
    }
  },
  computed: {
    isFormValid() {
      // Validar RUT usando el nuevo mixin
      const rutValidation = this.validateRutChile(this.form.rut);
      
      // Verificar que no hay mensajes de error visibles
      const hasVisibleErrors = document.querySelectorAll('#studentForm .form-group.error').length > 0;
      
      // Validar formato de email
      const emailValid = this.form.email.trim() && this.VALIDATION_REGEX.email.test(this.form.email);
      
      // Validar formato de teléfono
      const phoneValid = this.form.phoneNumber.trim() && this.VALIDATION_REGEX.phone.test(this.form.phoneNumber);
      
      return (
        this.form.studentName.trim() &&
        this.form.rut.trim() &&
        rutValidation.isValid &&
        emailValid &&
        phoneValid &&
        this.form.birthDate.trim() &&
        this.form.averageScore.trim() &&
        this.form.schoolRegionId &&
        this.form.schoolCommuneId &&
        this.form.schoolId &&
        this.form.graduationYear &&
        this.termsAccepted &&
        !hasVisibleErrors
      );
    },
    isFreeOrder() {
      // Verificar si la orden es gratuita basándose en el precio total
      // Los datos de precios están en stepperData.step4.quote
      const step4 = this.stepperData?.step4 || {};
      const prices = step4.quote || {};
      return prices.precioTotal === 0 || prices.precioTotal === '0';
    },
    buttonText() {
      return this.isFreeOrder ? 'Confirmar Matrícula' : 'Continuar a pagar';
    },
    selectYears() {
      let currentYear = new Date().getFullYear();
      return Array(33)
        .fill({})
        .map((x, i) => ({
          value: currentYear + 5 - i,
          label: (currentYear + 5 - i).toString()
        }));
    },
    isEmailSuggested() {
      return this.studentEmail && this.form.email === this.studentEmail;
    }
  },
  watch: {
    studentEmail: {
      immediate: true,
      handler(newEmail) {
        if (newEmail) {
          this.form.email = newEmail;
        }
      }
    }
  },
  methods: {
    async onSelectSchoolRegion(selectedRegion) {
      // Evitar llamadas múltiples si no hay cambio real
      const newRegionId = selectedRegion ? selectedRegion.id : null;
      if (newRegionId === this.form.schoolRegionId) {
        return;
      }
      
      this.availableCommunes = [];
      this.availableSchools = [];
      this.selectedSchoolCommune = null;
      this.selectedSchool = null;
      this.form.schoolRegionId = newRegionId;
      this.form.schoolCommuneId = null;
      this.form.schoolId = null;
      
      const regionId = this.form.schoolRegionId;
      if (regionId) {
        try {
          const { data: communes } = await this.Axios.get(`/communes/${regionId}`);
          this.availableCommunes = orderBy([...communes], "name");
        } catch (error) {
          console.error('Error loading communes:', error);
        }
      }
    },
    async onSelectSchoolCommune(selectedCommune) {
      // Evitar llamadas múltiples si no hay cambio real
      const newCommuneId = selectedCommune ? selectedCommune.id : null;
      if (newCommuneId === this.form.schoolCommuneId) {
        return;
      }
      
      this.availableSchools = [];
      this.selectedSchool = null;
      this.form.schoolCommuneId = newCommuneId;
      this.form.schoolId = null;
      
      const communeId = this.form.schoolCommuneId;
      if (communeId) {
        try {
          const { data: schools } = await this.Axios.get(`/colleges/${communeId}`);
          this.availableSchools = orderBy([...schools], "name");
        } catch (error) {
          console.error('Error loading schools:', error);
        }
      }
    },
    onSelectSchool(selectedSchool) {
      this.form.schoolId = selectedSchool ? selectedSchool.id : null;
    },
    onSelectGraduationYear(selectedYear) {
      this.form.graduationYear = selectedYear ? selectedYear.value : null;
    },
    
    setupCustomValidation() {
      // Configurar validaciones personalizadas sin jQuery validator automático
      const form = document.getElementById('studentForm');
      if (form) {
        // Remover cualquier validación automática existente
        $(form).off('submit');
        
        // Agregar validación personalizada en submit
        $(form).on('submit', (e) => {
          e.preventDefault();
          if (this.validateFormManually()) {
            this.$emit('form-valid', this.form);
          }
        });
        
        // Agregar validaciones en tiempo real para cada campo
        this.setupFieldValidations();
      }
    },
    
    setupFieldValidations() {
      // Validación en tiempo real para nombre del estudiante
      const studentNameField = document.getElementById('studentName');
      if (studentNameField) {
        studentNameField.addEventListener('blur', () => {
          this.validateStudentName();
        });
        studentNameField.addEventListener('input', () => {
          this.validateStudentName();
        });
      }
      
      // Validación en tiempo real para RUT - MANEJADA POR EL MIXIN
      // No agregar validación manual aquí para evitar duplicación de mensajes
      
      // Validación en tiempo real para email
      const emailField = document.getElementById('emailStudent');
      if (emailField) {
        emailField.addEventListener('blur', () => {
          this.validateEmail();
        });
        emailField.addEventListener('input', () => {
          this.validateEmail();
        });
      }
      
      // Validación en tiempo real para fecha de nacimiento
      const birthDateField = document.getElementById('birthDate');
      if (birthDateField) {
        birthDateField.addEventListener('blur', () => {
          this.validateBirthDate();
        });
        birthDateField.addEventListener('input', () => {
          this.validateBirthDate();
        });
      }
      
      // Validación en tiempo real para promedio
      const averageScoreField = document.getElementById('averageScore');
      if (averageScoreField) {
        averageScoreField.addEventListener('blur', () => {
          this.validateAverageScore();
        });
        averageScoreField.addEventListener('input', () => {
          this.validateAverageScore();
        });
      }
      
      // Validación en tiempo real para teléfono
      const phoneField = document.getElementById('phoneNumber');
      if (phoneField) {
        phoneField.addEventListener('blur', () => {
          this.validatePhone();
        });
        phoneField.addEventListener('input', () => {
          this.validatePhone();
        });
      }
    },
    
    showFieldError(fieldName, message) {
      const field = document.getElementById(fieldName);
      if (field) {
        const formGroup = field.closest('.form-group');
        if (formGroup) {
          formGroup.classList.add('error');
          
          // Remover mensaje de error existente
          formGroup.querySelectorAll('label.error').forEach(label => label.remove());
          
          // Crear nuevo mensaje de error
          const errorLabel = document.createElement('label');
          errorLabel.className = 'error';
          errorLabel.textContent = message;
          errorLabel.style.color = '#ef4444';
          errorLabel.style.fontSize = '0.75rem';
          errorLabel.style.marginTop = '0.25rem';
          errorLabel.style.fontWeight = '500';
          errorLabel.style.fontStyle = 'normal';
          errorLabel.style.fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
          
          // Insertar mensaje al final del formGroup
          formGroup.appendChild(errorLabel);
        }
      }
    },
    
    clearFieldError(fieldName) {
      const field = document.getElementById(fieldName);
      if (field) {
        const formGroup = field.closest('.form-group');
        if (formGroup) {
          formGroup.classList.remove('error');
          formGroup.querySelectorAll('label.error, .validation-message').forEach(label => label.remove());
        }
      }
    },
    
    validateStudentName() {
      if (!this.form.studentName || this.form.studentName.trim() === '') {
        this.showFieldError('studentName', VALIDATION_MESSAGES.required.nombres);
      } else if (this.form.studentName.length < 2) {
        this.showFieldError('studentName', VALIDATION_MESSAGES.length.nombres.min);
      } else {
        this.clearFieldError('studentName');
      }
    },
    

    
    validateEmail() {
      if (!this.form.email || this.form.email.trim() === '') {
        this.showFieldError('emailStudent', VALIDATION_MESSAGES.required.email);
      } else if (!VALIDATION_REGEX.email.test(this.form.email)) {
        this.showFieldError('emailStudent', VALIDATION_MESSAGES.format.email);
      } else {
        this.clearFieldError('emailStudent');
      }
    },
    
    validateBirthDate() {
      if (!this.form.birthDate || this.form.birthDate.trim() === '') {
        this.showFieldError('birthDate', VALIDATION_MESSAGES.required.fechaNacimiento);
      } else if (this.form.birthDate.length < 10) {
        this.showFieldError('birthDate', VALIDATION_MESSAGES.format.fecha);
      } else {
        this.clearFieldError('birthDate');
      }
    },
    

    
    validatePhone() {
      if (!this.form.phoneNumber || this.form.phoneNumber.trim() === '') {
        // Campo vacío - mostrar error de requerido
        this.showFieldError('phoneNumber', VALIDATION_MESSAGES.required.phoneNumber);
      } else if (!VALIDATION_REGEX.phone.test(this.form.phoneNumber)) {
        // Formato inválido
        this.showFieldError('phoneNumber', VALIDATION_MESSAGES.format.phoneNumber);
      } else {
        // Teléfono válido
        this.clearFieldError('phoneNumber');
      }
    },
    validateAverageScore() {
      // Obtener solo números del input
      let numbers = this.form.averageScore.replace(/[^0-9]/g, '');
      
      // Aplicar máscara automática
      if (numbers.length > 0) {
        // Si hay más de 1 dígito, agregar coma después del primer dígito
        if (numbers.length >= 2) {
          this.form.averageScore = numbers.charAt(0) + ',' + numbers.charAt(1);
        } else {
          // Si solo hay 1 dígito, mostrarlo tal como está
          this.form.averageScore = numbers;
        }
      } else {
        this.form.averageScore = '';
      }
      
      // Validar rango (0,0 a 7,0) - convertir temporalmente para validar
      const valueForValidation = this.form.averageScore.replace(',', '.');
      const value = parseFloat(valueForValidation);
      if (value < 0 || value > 7) {
        this.form.averageScore = '';
      }
      
      // Mostrar mensaje de error si está vacío
      if (!this.form.averageScore || this.form.averageScore.trim() === '') {
        this.showFieldError('averageScore', VALIDATION_MESSAGES.required.promedio);
      } else {
        this.clearFieldError('averageScore');
      }
    },
    handleSubmit() {
      this.isSubmitting = true;
      
      // Ejecutar validaciones en tiempo real para todos los campos
      this.validateAllFields();
      
      // Validación manual completa
      const isValid = this.validateFormManually();
      
      if (isValid) {
        // Formatear el promedio antes de enviar (convertir comas a puntos)
        const formData = { ...this.form };
        if (formData.averageScore) {
          formData.averageScore = formData.averageScore.replace(/,/g, '.');
        }
        
        // Agregar el nombre del colegio si está seleccionado
        if (this.selectedSchool && this.selectedSchool.name) {
          formData.schoolName = this.selectedSchool.name;
        }
        
        this.$emit('form-completed', formData);
      } else {
        this.isSubmitting = false;
      }
    },
    
    validateAllFields() {
      // Validar nombre del estudiante
      if (!this.form.studentName || this.form.studentName.trim() === '') {
        this.showFieldError('studentName', VALIDATION_MESSAGES.required.nombres);
      } else if (this.form.studentName.length < 2) {
        this.showFieldError('studentName', VALIDATION_MESSAGES.length.nombres.min);
      } else {
        this.clearFieldError('studentName');
      }
      
      // Validar RUT - MANEJADO POR EL MIXIN
      // No agregar validación manual aquí para evitar duplicación de mensajes
      
      // Validar email
      if (!this.form.email || this.form.email.trim() === '') {
        this.showFieldError('emailStudent', VALIDATION_MESSAGES.required.email);
      } else if (!VALIDATION_REGEX.email.test(this.form.email)) {
        this.showFieldError('emailStudent', VALIDATION_MESSAGES.format.email);
      } else {
        this.clearFieldError('emailStudent');
      }
      
      // Validar teléfono
      if (!this.form.phoneNumber || this.form.phoneNumber.trim() === '') {
        this.showFieldError('phoneNumber', VALIDATION_MESSAGES.required.phoneNumber);
      } else if (!VALIDATION_REGEX.phone.test(this.form.phoneNumber)) {
        this.showFieldError('phoneNumber', VALIDATION_MESSAGES.format.phoneNumber);
      } else {
        this.clearFieldError('phoneNumber');
      }
      
      // Validar fecha de nacimiento
      if (!this.form.birthDate || this.form.birthDate.trim() === '') {
        this.showFieldError('birthDate', VALIDATION_MESSAGES.required.fechaNacimiento);
      } else if (this.form.birthDate.length < 10) {
        this.showFieldError('birthDate', VALIDATION_MESSAGES.format.fecha);
      } else {
        this.clearFieldError('birthDate');
      }
      
      // Validar promedio
      if (!this.form.averageScore || this.form.averageScore.trim() === '') {
        this.showFieldError('averageScore', VALIDATION_MESSAGES.required.promedio);
      } else {
        this.clearFieldError('averageScore');
      }
      
      // Validar región del colegio
      if (!this.form.schoolRegionId) {
        this.showFieldError('schoolRegion', VALIDATION_MESSAGES.required.region);
      } else {
        this.clearFieldError('schoolRegion');
      }
      
      // Validar comuna del colegio
      if (!this.form.schoolCommuneId) {
        this.showFieldError('schoolCommune', VALIDATION_MESSAGES.required.comuna);
      } else {
        this.clearFieldError('schoolCommune');
      }
      
      // Validar colegio
      if (!this.form.schoolId) {
        this.showFieldError('school', VALIDATION_MESSAGES.required.colegio);
      } else {
        this.clearFieldError('school');
      }
      
      // Validar año de graduación
      if (!this.form.graduationYear) {
        this.showFieldError('graduationYear', VALIDATION_MESSAGES.required.añoGraduacion);
      } else {
        this.clearFieldError('graduationYear');
      }
    },
    
    validateFormManually() {
      let isValid = true;
      
      // Validar nombre del estudiante
      if (!this.form.studentName || this.form.studentName.trim() === '') {
        this.showFieldError('studentName', VALIDATION_MESSAGES.required.nombres);
        isValid = false;
      } else if (this.form.studentName.length < 2) {
        this.showFieldError('studentName', VALIDATION_MESSAGES.length.nombres.min);
        isValid = false;
      } else {
        this.clearFieldError('studentName');
      }
      
      // Validar RUT - MANEJADO POR EL MIXIN
      // No agregar validación manual aquí para evitar duplicación de mensajes
      
      // Validar email
      if (!this.form.email || this.form.email.trim() === '') {
        this.showFieldError('emailStudent', VALIDATION_MESSAGES.required.email);
        isValid = false;
      } else if (!VALIDATION_REGEX.email.test(this.form.email)) {
        this.showFieldError('emailStudent', VALIDATION_MESSAGES.format.email);
        isValid = false;
      } else {
        this.clearFieldError('emailStudent');
      }
      
      // Validar teléfono
      if (!this.form.phoneNumber || this.form.phoneNumber.trim() === '') {
        this.showFieldError('phoneNumber', VALIDATION_MESSAGES.required.phoneNumber);
        isValid = false;
      } else if (!VALIDATION_REGEX.phone.test(this.form.phoneNumber)) {
        this.showFieldError('phoneNumber', VALIDATION_MESSAGES.format.phoneNumber);
        isValid = false;
      } else {
        this.clearFieldError('phoneNumber');
      }
      
      // Validar fecha de nacimiento
      if (!this.form.birthDate || this.form.birthDate.trim() === '') {
        this.showFieldError('birthDate', VALIDATION_MESSAGES.required.fechaNacimiento);
        isValid = false;
      } else if (this.form.birthDate.length < 10) {
        this.showFieldError('birthDate', VALIDATION_MESSAGES.format.fecha);
        isValid = false;
      } else {
        this.clearFieldError('birthDate');
      }
      
      // Validar promedio
      if (!this.form.averageScore || this.form.averageScore.trim() === '') {
        this.showFieldError('averageScore', VALIDATION_MESSAGES.required.promedio);
        isValid = false;
      } else {
        this.clearFieldError('averageScore');
      }
      
      // Validar región del colegio
      if (!this.form.schoolRegionId) {
        this.showFieldError('schoolRegion', VALIDATION_MESSAGES.required.region);
        isValid = false;
      } else {
        this.clearFieldError('schoolRegion');
      }
      
      // Validar comuna del colegio
      if (!this.form.schoolCommuneId) {
        this.showFieldError('schoolCommune', VALIDATION_MESSAGES.required.comuna);
        isValid = false;
      } else {
        this.clearFieldError('schoolCommune');
      }
      
      // Validar colegio
      if (!this.form.schoolId) {
        this.showFieldError('school', VALIDATION_MESSAGES.required.colegio);
        isValid = false;
      } else {
        this.clearFieldError('school');
      }
      
      // Validar año de graduación
      if (!this.form.graduationYear) {
        this.showFieldError('graduationYear', VALIDATION_MESSAGES.required.añoGraduacion);
        isValid = false;
      } else {
        this.clearFieldError('graduationYear');
      }
      
      // Validar términos y condiciones
      if (!this.termsAccepted) {
        // No hay campo específico para mostrar error, pero podemos usar un mensaje general
        isValid = false;
      }
      return isValid;
    },
    
    // Cargar datos iniciales
    async loadInitialData() {
      // Evitar cargar múltiples veces
      if (this.dataLoaded) {
        return;
      }
      
      if (this.initialData && Object.keys(this.initialData).length > 0) {
        // Mapear los datos del store a los campos del formulario
        this.form.studentName = this.initialData.studentName || '';
        this.form.rut = this.initialData.rut || '';
        this.form.email = this.initialData.email || this.studentEmail || '';
        this.form.phoneNumber = this.initialData.phoneNumber || '';
        this.form.birthDate = this.initialData.birthDate || '';
        // Convertir promedio NEM de punto a coma para mostrar correctamente
        const averageScore = this.initialData.averageScore || '';
        this.form.averageScore = averageScore.replace(/\./g, ',');
        this.form.schoolRegionId = this.initialData.schoolRegionId || null;
        this.form.schoolCommuneId = this.initialData.schoolCommuneId || null;
        this.form.schoolId = this.initialData.schoolId || null;
        this.form.graduationYear = this.initialData.graduationYear || null;
        this.form.authorizationPublicitary = true;
        
        // Cargar las regiones, comunas y colegios si existen
        if (this.initialData.schoolRegionId) {
          // Buscar el objeto región completo basándose en el ID
          const region = this.availableRegions.find(r => r.id === this.initialData.schoolRegionId);
          if (region) {
            this.selectedSchoolRegion = region;
          }
          
          // Establecer el schoolRegionId antes de cargar las comunas
          this.form.schoolRegionId = this.initialData.schoolRegionId;
          
          // Simular la selección de región para cargar comunas
          await this.onSelectSchoolRegion({ id: this.initialData.schoolRegionId });
          
          // Después de cargar las comunas, establecer el schoolCommuneId
          if (this.initialData.schoolCommuneId) {
            // Buscar el objeto comuna completo basándose en el ID
            const commune = this.availableCommunes.find(c => c.id === this.initialData.schoolCommuneId);
            if (commune) {
              this.selectedSchoolCommune = commune;
              // Cargar los colegios de la comuna seleccionada
              await this.onSelectSchoolCommune({ id: this.initialData.schoolCommuneId });
              
              // Después de cargar los colegios, establecer el schoolId
              if (this.initialData.schoolId) {
                // Buscar el objeto colegio completo basándose en el ID
                const school = this.availableSchools.find(s => s.id === this.initialData.schoolId);
                if (school) {
                  this.selectedSchool = school;
                }
              }
            }
          }
        }
      } else if (this.studentEmail) {
        // Si no hay datos iniciales pero hay email del estudiante, usarlo
        this.form.email = this.studentEmail;
      }
      
      this.dataLoaded = true;
      
      // Validar el campo de celular después de cargar los datos iniciales
      if (this.form.phoneNumber) {
        this.validatePhone();
      }
    }
  },
  async created() {
    try {
      const { data: regions } = await this.Axios.get("/regions");
      this.availableRegions = orderBy([...regions], "name");
    } catch (error) {
      console.error('Error loading regions:', error);
    }
  },
  mounted() {
    // Cargar datos iniciales si existen
    this.loadInitialData();
    
    // Usar solo nuestras validaciones personalizadas sin jQuery validator automático
    this.setupCustomValidation();
  },
}
</script>

<style scoped lang="scss">
@import '@/assets/scss/_form-validation.scss';
@import '@/assets/scss/_inputs-shared.scss';
@import '@/assets/scss/_rut-validation.scss';
/* Container */
.student-form-container {
  width: 100%;
  padding: 2rem 1rem;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

.form-wrapper {
  max-width: 800px;
  margin: 0 auto;
  background: white;
  border-radius: 12px;
  padding: 2rem;
}

/* Header Section */
.header-section {
  text-align: center;
  margin-bottom: 2rem;
}

.form-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 0.5rem 0;
  line-height: 1.2;
}

.form-subtitle {
  font-size: 1rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
}

/* Form Content */
.form-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}

/* Form Group - Para compatibilidad con validaciones */
.form-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 8px;
}

/* Estilos de error manejados por _form-validation.scss */

/* Form field styles */
.form-field {
  margin-bottom: 1.5rem;
}

.field-label {
  font-family: "Poppins-Medium";
  display: block;
  margin-bottom: 0.5rem;
  font-size: 14px;
  font-weight: 100;
  color: #353535;
}

/* Estilos de inputs manejados por _inputs-shared.scss */

/* Hidden inputs for validation */
.input-hidden {
  position: absolute;
  left: -3000px;
  visibility: hidden;
  z-index: -1000;
}

/* Estilos de multiselect manejados por _inputs-shared.scss */

/* Estilos de opciones de multiselect manejados por _inputs-shared.scss */

/* Error states for multiselect */
/* Estilos de multiselect manejados por _form-validation.scss */

/* Form Separator */
.form-separator {
  width: 100%;
  height: 1px;
  background-color: #e5e7eb;
}

/* Terms and Conditions Styles */
.terms-conditions-section {
  margin-top: 1rem;
  margin-bottom: 1rem;
}

.terms-checkbox-container {
  display: flex;
  align-items: flex-start;
  cursor: pointer;
  font-size: 14px;
  line-height: 1.4;
  color: #374151;
  position: relative;
  padding-left: 30px;
}

.terms-checkbox {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.checkmark {
  position: absolute;
  top: 2px;
  left: 0;
  height: 18px;
  width: 18px;
  background-color: #ffffff;
  border: 2px solid #000000;
  border-radius: 3px;
  transition: all 0.2s ease;
}

.terms-checkbox:checked ~ .checkmark {
  background-color: #000000;
  border-color: #000000;
}

.checkmark:after {
  content: "";
  position: absolute;
  display: none;
}

.terms-checkbox:checked ~ .checkmark:after {
  display: block;
}

.terms-checkbox-container .checkmark:after {
  left: 5px;
  top: 1px;
  width: 6px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.terms-text {
  display: block;
  margin-left: 0;
  line-height: 1.4;
}

.terms-link {
  color: #3b82f6;
  text-decoration: underline;
  font-weight: 500;
}

.terms-link:hover {
  color: #1d4ed8;
  text-decoration: none;
}

.terms-checkbox-container:hover .checkmark {
  border-color: #374151;
}

.terms-checkbox:disabled ~ .checkmark {
  background-color: #f3f4f6;
  border-color: #d1d5db;
  cursor: not-allowed;
}

.terms-checkbox:disabled ~ .terms-text {
  color: #9ca3af;
  cursor: not-allowed;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  margin-top: 1rem;
}

.back-button,
.continue-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  height: 48px;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 500;
  border-radius: 100px;
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;
  font-family: inherit;
}

.back-button {
  background: transparent;
  border: 2px solid #ccc;
  color: #374151;
  flex: 1;
}

.back-button:hover {
  border-color: #9ca3af;
  background-color: #f9fafb;
}

.continue-button {
  background-color: #000000;
  border: 2px solid #000000;
  color: #ffffff;
  flex: 1;
}

.continue-button:hover:not(:disabled) {
  background-color: #1f2937;
  border-color: #1f2937;
}

.continue-button:disabled {
  background-color: #9ca3af;
  border-color: #9ca3af;
  cursor: not-allowed;
  opacity: 0.6;
}

.button-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

/* Error message for NEM */
.error-message {
  color: #ff0000 !important;
  font-size: 14px;
  font-weight: 400;
  margin-top: 0.25rem;
}

/* Suggestion text */
.suggestion-text {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 400;
}

.suggestion-text i {
  color: #0ea5e9;
  font-size: 1rem;
}

/* Responsive Design */
@media (min-width: 768px) {
  .form-row {
    grid-template-columns: 1fr 1fr;
  }
  
  .form-field.full-width {
    grid-column: 1 / -1;
  }
  
  .student-form-container {
    padding: 2rem;
  }
  
  .form-wrapper {
    padding: 2.5rem;
  }
}

@media (max-width: 767px) {
  .form-wrapper {
    padding: 1.5rem;
    margin: 0 0.5rem;
  }
  
  .form-title {
    font-size: 1.5rem;
  }
  
  .student-form-container {
    padding: 1rem 0.5rem;
  }
  
  .action-buttons {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .terms-text {
    font-size: 13px;
  }
  
  .terms-checkbox-container {
    padding-left: 28px;
  }
  
  .checkmark {
    height: 16px;
    width: 16px;
  }
  
  .terms-checkbox-container .checkmark:after {
    left: 4px;
    top: 1px;
    width: 5px;
    height: 8px;
  }
  
  .back-button,
  .continue-button {
    width: 100%;
  }
}

/* Completed Form State */
.completed-badge {
  display: inline-block;
  background-color: #10b981;
  color: white;
  font-size: 0.875rem;
  font-weight: 500;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  margin-left: 1rem;
  vertical-align: middle;
}

/* Single Field Style */
.single-field {
  max-width: 250px;
}

.form-completed .action-buttons {
  display: none;
}

</style>